import SideMenu from "../../../ui/SideMenu";
import styles from "../../../styles/Components/bot/bot-backtest/RequestBacktestMenu.module.css";
import { useDispatch, useSelector } from "react-redux";
import { Button, message, Radio, Skeleton, Slider } from "antd";
import CreateBot from "../CreateBot";
import { useEffect, useState } from "react";
import { MdMoreHoriz, MdOutlineExpandLess } from "react-icons/md";
import BacktestTimeSettings from "./BacktestTimeSettings";
import moment from "moment/moment";
import { TbCalendarSearch } from "react-icons/tb";
import { BiStoreAlt } from "react-icons/bi";
import { PiSlidersHorizontalBold } from "react-icons/pi";
import { createBacktest } from "../../../redux/bot/actions";
import useUserData from "../../../hooks/useUserData";
import { closeSideMenu } from "../../../redux/ui/uiSlice";

const formatDate = (date) => {
  return moment(date).format("YYYY-MM-DD HH:mm:ss");
};

function RequestBacktestMenu({ bot, closeHandler }) {
  const {
    dashboard: {
      botInfoStatus,
      overallBotPerformance: { bots_performance: bots = [] },
    },
    profile: { profileData },
    botState: { createBacktestLoading },
  } = useSelector((state) => state);

  const botList = [...bots]?.sort((a, _) => (a?.status === "Active" ? -1 : 1));

  const botsCount = bots?.length;
  const { username, email } = useUserData();
  const dispatch = useDispatch();

  const [selectedBot, setSelectedBot] = useState("");
  const [showMore, setShowMore] = useState(false);

  const [selectedDates, setSelectedDates] = useState([]);
  const [dateError, setDateError] = useState("");
  const [selectedRange, setSelectedRange] = useState(null);
  const [marketType, setMarketType] = useState("spot");
  const [marginLeverage, setMarginLeverage] = useState("1");

  const disabledSubmit =
    createBacktestLoading || !selectedBot || selectedDates?.length !== 2;

  const targetBot = botList?.find(
    (bot) => bot?.bot_creation_id === selectedBot,
  )?.bot_name;

  const displayBotsCount = showMore ? botsCount : 2;

  const resetForm = () => {
    dispatch(closeSideMenu());
    setSelectedBot("");
    setShowMore(false);
    setDateError("");
    setMarketType("spot");
    setSelectedDates([]);
    setSelectedRange(null);
    setMarginLeverage("1");
  };

  const selectBotHandler = (e) => {
    setSelectedBot(e.target.value);
  };

  const handleSelectedTime = (num) => {
    setSelectedRange(num);

    const endDate = moment();
    const startDate = moment().subtract(num, num === 1 ? "month" : "days");
    setSelectedDates([startDate, endDate]);
  };

  const handleSubmit = async () => {
    if (
      // eslint-disable-next-line no-mixed-operators
      !selectedBot ||
      // eslint-disable-next-line no-mixed-operators
      !(selectedDates && selectedDates[0] && selectedDates[1]) ||
      createBacktestLoading
    ) {
      return;
    }

    const startDate = moment(new Date(selectedDates[0]));
    const endDate = moment(new Date(selectedDates[1]));
    const diff = moment(endDate).diff(moment(startDate), "days");

    if (profileData?.is_admin !== "True" && (diff < 7 || diff > 60)) {
      message.info(
        "Please select a date range with either one week or less than two months space.",
      );
      return;
    }

    await dispatch(
      createBacktest(
        username,
        email,
        selectedBot,
        formatDate(startDate),
        formatDate(endDate),
        marketType,
        marginLeverage,
      ),
    ).then(() => {
      if (closeHandler) {
        closeHandler();
      }
      resetForm();
    });
  };

  useEffect(() => {
    if (profileData?.is_admin === "True") return;
    if (selectedDates && selectedDates.length) {
      const startDate = moment(new Date(selectedDates[0]));
      const endDate = moment(new Date(selectedDates[1]));
      const durationInDays = moment(endDate).diff(moment(startDate), "days");

      if (durationInDays < 14 || durationInDays > 100) {
        setDateError(
          "Please select a date range with a space between 1 week or 2 months.",
        );
      } else {
        setDateError("");
      }
    }
  }, [selectedDates, profileData?.is_admin]);

  useEffect(() => {
    if (bot) {
      setSelectedBot(bot);
    }
  }, [bot]);

  return (
    <SideMenu
      title={`Request backtest ${selectedBot ? `for: ${targetBot}` : ""}`}
      scope="request-backtest"
    >
      <div className={styles.container}>
        {!bot && (
          <div className={styles.field}>
            <h3 className={styles.title}>
              Select bot to run backtest with bot parameters
            </h3>
            {botInfoStatus === "loading" ? (
              <div className={styles.loader}>
                {Array.from({ length: 3 })?.map((_, i) => (
                  <Skeleton.Node key={i} className={styles.loader} active />
                ))}
              </div>
            ) : botsCount === 0 ? (
              <div className={styles.noBot}>
                <span>You don't have any bot.</span>
                <CreateBot title="Create Now" className={styles.createBtn} />
              </div>
            ) : (
              <>
                <div className={styles.botsList}>
                  <Radio.Group
                    className={styles.botItems}
                    value={selectedBot}
                    onChange={selectBotHandler}
                  >
                    {botList?.slice(0, displayBotsCount)?.map((bot) => (
                      <Radio
                        key={bot?.bot_creation_id}
                        value={bot?.bot_creation_id}
                        className={styles.botItem}
                      >
                        <span className={styles.botName}>{bot?.bot_name}</span>
                        {bot?.status === "Active" && (
                          <span className={styles.botStatus}>Active</span>
                        )}
                      </Radio>
                    ))}
                  </Radio.Group>
                </div>
                {botsCount > 2 && (
                  <span
                    className={styles.moreBtn}
                    onClick={() => setShowMore((prev) => !prev)}
                  >
                    View {showMore ? "Less" : "More"}
                    {showMore ? <MdOutlineExpandLess /> : <MdMoreHoriz />}
                  </span>
                )}
              </>
            )}
          </div>
        )}

        <div className={styles.field}>
          <h3 className={styles.title}>
            <TbCalendarSearch
              color="#0c4cfc"
              size={18}
              style={{ marginRight: "10px" }}
            />
            Choose the Start and End date
          </h3>
          <BacktestTimeSettings
            selectedBot={selectedBot}
            setSelectedDates={setSelectedDates}
            selectedDate={selectedDates}
          >
            {dateError && (
              <p
                style={{
                  color: "red",
                  margin: ".2rem .5rem",
                  fontSize: "12px",
                }}
              >
                {dateError}
              </p>
            )}
          </BacktestTimeSettings>

          <div className={styles.timeBtns}>
            {/*<span*/}
            {/*  className={selectedRange === 7 ? styles.activeBtn : ""}*/}
            {/*  onClick={() => handleSelectedTime(7)}*/}
            {/*>*/}
            {/*  Last week*/}
            {/*</span>*/}
            <span
              className={selectedRange === 14 ? styles.activeBtn : ""}
              onClick={() => handleSelectedTime(14)}
            >
              Last 2 weeks
            </span>
            <span
              className={selectedRange === 21 ? styles.activeBtn : ""}
              onClick={() => handleSelectedTime(21)}
            >
              Last 3 weeks
            </span>
            <span
              className={selectedRange === 1 ? styles.activeBtn : ""}
              onClick={() => handleSelectedTime(1)}
            >
              Last month
            </span>
          </div>
        </div>

        <div className={styles.field}>
          <h3 className={styles.title}>
            {" "}
            <BiStoreAlt
              color="#0c4cfc"
              size={18}
              style={{ marginRight: "10px" }}
            />
            Market Type
          </h3>

          <Radio.Group
            className={styles.markets}
            value={marketType}
            onChange={(e) => setMarketType(e.target.value)}
          >
            <Radio className={styles.marketItem} value={"spot"}>
              Spot
            </Radio>
            <Radio className={styles.marketItem} value={"futures"}>
              Futures
            </Radio>
          </Radio.Group>
        </div>

        <div className={styles.field}>
          <div className={styles.fieldHeader}>
            <h3 className={styles.title}>
              <PiSlidersHorizontalBold
                color="#0c4cfc"
                size={18}
                style={{ marginRight: "10px" }}
              />
              Margin Leverage
            </h3>
            <span className={styles.leverageTip}>{marginLeverage}x</span>
          </div>
          <Slider
            min={1}
            max={10}
            value={+marginLeverage}
            onChange={(e) => setMarginLeverage(`${e}`)}
            styles={{
              rail: {
                height: "8px",
                backgroundColor: "#E7E7E7",
                borderRadius: "10px",
              },
              handle: { height: "14px" },
              track: { height: "8px", borderRadius: "10px" },
            }}
            className={styles.slider}
          />
        </div>

        <Button
          className={styles.submitBtn}
          disabled={Boolean(disabledSubmit)}
          onClick={handleSubmit}
          loading={createBacktestLoading}
        >
          Submit
        </Button>
      </div>
    </SideMenu>
  );
}

export default RequestBacktestMenu;
